import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { M, type MessageKey } from "./messages";

export type Locale = "en" | "ar";
const STORAGE_KEY = "walashy.locale";

function initialLocale(): Locale {
  return document.documentElement.lang === "ar" ? "ar" : "en";
}

type I18n = {
  locale: Locale;
  dir: "ltr" | "rtl";
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  money: (cents: number | null | undefined, currency?: string) => string;
  date: (iso: string | Date, opts?: Intl.DateTimeFormatOptions) => string;
  relative: (iso: string) => string;
};

/** The official Saudi Riyal sign (U+20C1, Unicode 17). Drawn by the bundled "Saudi Riyal" font. */
export const RIYAL = "\u20c1";

const Ctx = createContext<I18n | null>(null);

let currentLocale: Locale = initialLocale();
/** For non-React code (the API client sends it so server errors come back in the same language). */
export const getLocale = () => currentLocale;

export function I18nProvider({ children, onChange }: { children: ReactNode; onChange?: (l: Locale) => void }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    currentLocale = locale;
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* private mode: preference just isn't remembered */
    }
  }, [locale]);

  const setLocale = useCallback(
    (l: Locale) => {
      setLocaleState(l);
      onChange?.(l);
    },
    [onChange],
  );

  const value = useMemo<I18n>(() => {
    // Saudi Arabic with Western digits and the Gregorian calendar (plain ar-SA defaults to Hijri
    // dates), so prices, quantities, dates and codes read the same in both languages.
    const tag = locale === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-u-ca-gregory";
    const t = (key: MessageKey, vars?: Record<string, string | number>) => {
      const s: string = M[key]?.[locale === "ar" ? 1 : 0] ?? key;
      return vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? "")) : s;
    };
    return {
      locale,
      dir: locale === "ar" ? "rtl" : "ltr",
      setLocale,
      t,
      // Amounts are wrapped in Unicode isolates (FSI…PDI) so "360 US$" never reorders inside
      // surrounding Arabic or English text.
      money: (cents, currency = "SAR") =>
        cents == null
          ? "—"
          : currency === "SAR"
            ? // SAMA rule: the riyal sign sits to the left of the number with a space. A left-to-right isolate
              // keeps it on the left inside Arabic text too; the no-break space keeps sign and number together.
              "\u2066" + RIYAL + "\u00a0" + new Intl.NumberFormat(tag, { maximumFractionDigits: cents % 100 === 0 ? 0 : 2, minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100) + "\u2069"
            : "\u2068" + new Intl.NumberFormat(tag, { style: "currency", currency, maximumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100) + "\u2069",
      date: (iso, opts = { day: "numeric", month: "short", year: "numeric" }) => new Intl.DateTimeFormat(tag, opts).format(new Date(iso)),
      relative: (iso) => {
        const diff = (new Date(iso).getTime() - Date.now()) / 1000;
        const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
        const abs = Math.abs(diff);
        if (abs < 60) return rtf.format(Math.round(diff), "second");
        if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
        if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
        return rtf.format(Math.round(diff / 86400), "day");
      },
    };
  }, [locale, setLocale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n outside provider");
  return v;
}

export function isMessageKey(k: string): k is MessageKey {
  return k in M;
}
